import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { KineticLines } from "@/components/KineticText";
import { flash } from "@/lib/flash";
import { Link } from "react-router-dom";

export const Hero = ({
  profile,
  showAvatar = true,
  cataloguePage = false,
}) => {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const videoY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const name = profile?.name || "DORIANTELIVE";
  const tagline =
    profile?.tagline || "STREAMER // BUILDER // SONGWRITER // COMEBACK IN PROGRESS";

  return (
    <section
      ref={ref}
      data-testid="hero-section"
          className={`
            relative
            flex
            flex-col
            items-center
            overflow-hidden
            px-6
            pt-16
            sm:pt-24
            sm:px-10
            lg:px-16
            ${showAvatar ? "min-h-[850px] lg:min-h-[558px] lg:block" : "pb-12"}
          `}
    >

          {/* Avatar video - mobile flow / desktop floating */}
          {showAvatar && (
          <motion.div
            style={{ y: videoY }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
           className="
              pointer-events-none
              relative
              z-10
              mb-8
              flex
              w-full
              justify-center
              lg:absolute
              lg:right-10
              lg:top-24
              lg:mb-0
              lg:w-auto
              lg:justify-start
              lg:-translate-y-0
            "
            data-testid="hero-avatar"
          >
            <div
            className="
              relative
              overflow-hidden
              h-[clamp(240px,70vw,440px)]
              w-[clamp(195px,55vw,360px)]
            "
            style={{
              clipPath:
                "polygon(18% 0, 100% 8%, 92% 100%, 0 88%)",
            }}
          >

              {/* Green glow layer */}
              <div
                className="absolute inset-0 z-10 mix-blend-screen"
                style={{
                  background:
                    "radial-gradient(circle at 40% 30%, rgba(0,255,136,0.25), transparent 60%)",
                }}
              />

              {/* WEBM */}
              <video
                src="/assets/MacknEyeProPic.webm"
                autoPlay
                loop
                muted
                playsInline
                className="h-full w-full object-cover object-center"
              />

              {/* Border overlay */}
              <div
                className="absolute inset-0 border border-matrix-green/40"
                style={{
                  clipPath:
                    "polygon(18% 0, 100% 8%, 92% 100%, 0 88%)",
                }}
              />
            </div>
          </motion.div>
          )}


      {/* Hero text */}
      <motion.div
          style={{ y: textY, opacity: fade }}
          className="relative z-20 w-full lg:w-[65%]"
        >

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-matrix-green green-glow"
        >
          <span className="inline-block h-2 w-2 animate-glow-pulse rounded-full bg-matrix-green" />

          <span data-testid="hero-status">
            // booting doriantelive.exe
          </span>
        </motion.div>


          <h1
            className="
              w-full
              overflow-hidden
              font-display
              text-[clamp(1.8rem,8vw,8.2rem)]
              font-black
              uppercase
              leading-[0.9]
              tracking-[-0.05em]
              lg:w-[calc(100vw-5rem)]
            "
          >
          <KineticLines
            testId="hero-wordmark"
            lines={[`${name}.COM`]}
            lineClassName="brand-glow"
          />
        </h1>


        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-6 max-w-xl font-mono text-sm leading-relaxed text-white/70 sm:text-base"
          data-testid="hero-tagline"
        >
          <span className="text-matrix-green">&gt;</span> {tagline}
        </motion.p>


        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          {cataloguePage && (
            <Link
              to="/"
              data-testid="hero-home-btn"
              className="inline-flex items-center gap-2 rounded-full border border-matrix-green/40 px-7 py-3 font-display text-sm font-bold uppercase tracking-wider text-matrix-green no-underline transition-all duration-300 hover:bg-matrix-green/10 hover:shadow-[0_0_24px_rgba(0,255,136,0.3)]"
            >
              Home
            </Link>
          )}

          <a
            href="https://www.twitch.tv/doriantelive"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-watch-btn"
            className="group relative inline-flex items-center gap-2 rounded-full border-2 border-matrix-red bg-matrix-red/10 px-7 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-matrix-red hover:shadow-[0_0_30px_rgba(255,0,0,0.5)]"
          >
            Watch Live on Twitch
          </a>

          {!cataloguePage && (
            <>
              <a
                href="#socials"
                data-testid="hero-explore-btn"
                className="inline-flex items-center gap-2 rounded-full border border-matrix-green/40 px-7 py-3 font-display text-sm font-bold uppercase tracking-wider text-matrix-green transition-all duration-300 hover:bg-matrix-green/10 hover:shadow-[0_0_24px_rgba(0,255,136,0.3)]"
              >
                Explore the Grid
              </a>

              <Link
                to="/song-catalogue"
                data-testid="hero-song-catalogue-btn"
                className="inline-flex items-center gap-2 rounded-full border border-matrix-orange/40 px-7 py-3 font-display text-sm font-bold uppercase tracking-wider text-matrix-orange no-underline transition-all duration-300 hover:bg-matrix-orange/10 hover:shadow-[0_0_24px_rgba(255,140,0,0.3)]"
              >
                Song Catalogue
              </Link>

              <Link
                to="/members"
                data-testid="hero-members-btn"
                className="inline-flex items-center gap-2 rounded-full border border-matrix-green/40 px-7 py-3 font-display text-sm font-bold uppercase tracking-wider text-matrix-green no-underline transition-all duration-300 hover:bg-matrix-green/10 hover:shadow-[0_0_24px_rgba(0,255,136,0.3)]"
              >
                Members
              </Link>

            </>
          )}

          <div className="relative inline-flex">
            {/* Heavenly energy layer */}
            <svg
              className="pointer-events-none absolute inset-0 z-20 h-full w-full overflow-visible"
              viewBox="0 0 100 48"
              preserveAspectRatio="none"
            >
              <defs>
                <filter
                  id="merchOrbGlow"
                  x="-200%"
                  y="-200%"
                  width="400%"
                  height="400%"
                >
                  <feGaussianBlur stdDeviation="2" result="blur1" />
                  <feGaussianBlur stdDeviation="5" result="blur2" />

                  <feMerge>
                    <feMergeNode in="blur2" />
                    <feMergeNode in="blur1" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>

                <path
                  id="merchPath"
                  d="
                    M 24 1
                    H 76
                    A 23 23 0 0 1 76 47
                    H 24
                    A 23 23 0 0 1 24 1
                    Z
                  "
                  fill="none"
                />
              </defs>

              <circle
                r="15"
                fill="#fff8d6"
                opacity="0.16"
                filter="url(#merchOrbGlow)"
              >
                <animateMotion
                  dur=".36s"
                  repeatCount="indefinite"
                  rotate="auto"
                >
                  <mpath href="#merchPath" />
                </animateMotion>
              </circle>

              <circle
                r="3.2"
                fill="#fffdf0"
                filter="url(#merchOrbGlow)"
              >
                <animateMotion
                  dur=".35s"
                  repeatCount="indefinite"
                  rotate="auto"
                >
                  <mpath href="#merchPath" />
                </animateMotion>
              </circle>
            </svg>

            <Link
              to="/comik-releaf"
              data-testid="hero-merch-btn"
              className="
                relative
                z-10
                inline-flex
                items-center
                gap-2
                rounded-full
                border
                border-matrix-green/70
                bg-matrix-green/[0.10]
                px-7
                py-3
                font-display
                text-sm
                font-bold
                uppercase
                tracking-wider
                text-white
                no-underline
                shadow-[0_0_12px_rgba(0,255,136,0.10)]
                transition-all
                duration-300
                hover:bg-matrix-green/[0.16]
                hover:border-matrix-green
                hover:shadow-[0_0_18px_rgba(0,255,136,0.18)]
                hover:scale-[1.03]
              "
            >
              Merch
            </Link>
          </div>

          <button
            type="button"
            onClick={() => flash("mail@doriantelive.com", "green")}
            data-testid="hero-contact-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3 font-display text-sm font-bold uppercase tracking-wider text-white outline-none transition-all duration-300 hover:border-white/70 hover:bg-white/5 hover:shadow-[0_0_24px_rgba(255,255,255,0.15)]"
          >
            Contact
          </button>
        </motion.div>

      </motion.div>


      {/* Scroll indicator */}
      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-6 z-20 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-white/40 sm:left-10 lg:left-16"
      >
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.6 }}
          className="inline-block"
        >
          &darr;
        </motion.span>

        scroll to decrypt
      </motion.div>

    </section>
  );
};